import { Link } from "react-router-dom";
import { Stethoscope, FlaskConical, Sparkles, ShieldCheck, Globe2, TrendingUp, ArrowRight } from "lucide-react";
import PageLayout from "../components/pages/PageLayout";
import PageHero from "../components/pages/PageHero";
import FeatureGrid from "../components/pages/FeatureGrid";
import SplitSection from "../components/pages/SplitSection";
import RelatedLinks from "../components/pages/RelatedLinks";
import { Reveal, SectionHeader } from "../components/site/Reveal";

const U = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&q=80&w=1200`;

const STATS = [
  { value: "10+", label: "Years of Experience" },
  { value: "100+", label: "Skincare, Ayurveda & Nutraceutical Formulations" },
  { value: "20+", label: "Product Categories" },
  { value: "5+", label: "Global Markets Supported" },
];

const CAPABILITIES = [
  { icon: Stethoscope, title: "Doctor-Led R&D", text: "Founded by doctors with hands-on R&D experience in both traditional Ayurvedic and modern cosmetic and nutraceutical formats." },
  { icon: FlaskConical, title: "Formulation-First", text: "Every product starts at the formula bench, so quality, stability and cost are engineered in from day one — not patched at the factory." },
  { icon: Sparkles, title: "Three Disciplines, One Studio", text: "Rare cross-domain depth across cosmetics, Ayurveda and nutraceuticals under a single roof, for coherent multi-category ranges." },
  { icon: TrendingUp, title: "Commercially Grounded", text: "Formulas engineered around your target cost, margin and positioning, with realistic manufacturing routes." },
  { icon: ShieldCheck, title: "NDA Protected", text: "Confidential development; custom formulas developed for you belong to your brand." },
  { icon: Globe2, title: "Export Capability", text: "Documentation, compliance and formulation tuning for international markets alongside India." },
];

const EXPERTISE = [
  { title: "Cosmetics", text: "Skincare, haircare and personal care built on modern actives, elegant textures and proven stability.", href: "/cosmetic-formulation-development" },
  { title: "Ayurveda & Wellness", text: "Classical and proprietary Ayurvedic and wellness products developed by Ayurvedic doctors.", href: "/wellness-product-development" },
  { title: "Nutraceuticals", text: "Capsules, gummies, powders and functional wellness formats — efficacious, compliant and market-ready.", href: "/services" },
];

const ABOUT_CRUMBS = [{ label: "About" }];
const APPROACH_PARAGRAPHS = [
  "We were founded by doctors who spent years in R&D developing both classical Ayurvedic preparations and modern cosmetic and nutraceutical formats. That dual fluency is the studio's foundation: we understand why a traditional formula works and how to make it stable, elegant and compliant for today's customer.",
  "Our process is deliberately formulation-first. Before a brand commits to packaging, manufacturing or marketing, the product itself has to be right — in performance, sensorials, stability and cost. We then carry that formula through testing, documentation and licensed manufacturing so nothing is lost between the lab and the shelf.",
];
const APPROACH_BULLETS = ["Ayurvedic doctors and formulation experts", "Custom and ready-to-launch formulas", "Licensed manufacturing network", "Testing and regulatory documentation", "Export-ready development", "NDA-protected engagements"];
const RELATED_PATHS = ["/services", "/private-label-cosmetics-india", "/contact"];

const StatsBand = () => (
  <section data-testid="about-stats" className="bg-powder/70 border-y border-navy/10">
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-5">
      {STATS.map(({ value, label }, i) => (
        <Reveal key={label} delay={i * 0.1} className="text-center bg-cream/95 rounded-xl px-5 py-7 shadow-[0_12px_32px_rgba(62,74,46,0.10)]">
          <p className="font-display font-semibold text-3xl sm:text-4xl text-navy leading-none">{value}</p>
          <p className="mt-3 text-[10px] sm:text-[11px] uppercase tracking-[0.12em] font-semibold text-charcoal/65 leading-relaxed">{label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const ExpertiseLinks = () => (
  <section data-testid="about-expertise" className="py-24 lg:py-32 bg-ivory">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <SectionHeader overline="Our Expertise" title={<>Three disciplines. <em className="italic font-medium">One studio.</em></>} />
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {EXPERTISE.map(({ title, text, href }, i) => (
          <Reveal key={title} delay={i * 0.1}>
            <Link to={href} data-testid={`about-expertise-link-${i}`} className="group block h-full rounded-xl border border-navy/15 bg-cream p-8 transition-colors duration-300 hover:bg-navy">
              <h3 className="font-display text-2xl text-navy transition-colors duration-300 group-hover:text-ivory">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-charcoal/75 transition-colors duration-300 group-hover:text-ivory/70">{text}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors duration-300 group-hover:text-ivory">
                Learn more <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <PageLayout testId="about-page">
      {(openContact) => (
        <>
          <PageHero
            overline="About The Formulation House"
            title="A doctor-led studio for"
            titleEm="exceptional products."
            intro="The Formulation House is a formulation and product development studio for beauty, wellness and Ayurvedic brands. We take products from first idea to market-ready launch — guided by Ayurvedic doctors and formulation experts, and grounded in what sells."
            image={U("photo-1492552181161-62217fc3076d")}
            imageAlt="Doctor-led formulation team at work"
            crumbs={ABOUT_CRUMBS}
            onContact={openContact}
          />
          <StatsBand />
          <SplitSection
            overline="Our Approach"
            title="Traditional knowledge,"
            titleEm="scientific rigour."
            paragraphs={APPROACH_PARAGRAPHS}
            bullets={APPROACH_BULLETS}
            image={U("photo-1636740599648-ae84f705fc2e")}
            imageAlt="Ayurvedic botanicals and laboratory glassware"
          />
          <FeatureGrid overline="Capabilities" title="What makes the studio" titleEm="different." items={CAPABILITIES} testId="about-capabilities" />
          <ExpertiseLinks />
          <RelatedLinks paths={RELATED_PATHS} />
        </>
      )}
    </PageLayout>
  );
}
